import { StackedAreaChart, type ChartMarker, type ChartSeries } from "@gladis/ui";
import { t, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { PriceDto, TransactionDto } from "../dto.js";

/**
 * What one share has been worth.
 *
 * ==========================================================================================
 * PRICE, NOT THE VALUE OF THE HOLDING — AND THE DIFFERENCE IS THE POINT
 * ==========================================================================================
 *
 * The portfolio page charts VALUE, which moves when the household buys more. This one charts PRICE,
 * which moves only when the market does. Plotting value here would answer "how much have we put in"
 * while appearing to answer "how has it done", and a household reading a rising line after a large
 * purchase would draw exactly the wrong conclusion.
 *
 * `lines` rather than `stacked`: one series, drawn from zero, its height its own value. A stacked
 * chart of a single series draws the same shape while implying a decomposition that does not exist.
 *
 * ==========================================================================================
 * BOTH QUESTIONS, ONE AT A TIME
 * ==========================================================================================
 *
 * "Price" answers how the instrument has done. "My holding" answers what this household's shares
 * have been worth, which is price times however many were owned at the time — so it steps whenever
 * they bought or sold.
 *
 * They are shown as a toggle rather than two lines, because they are in different units: euros per
 * share against euros. Two series on one axis would put a €300 share price and a €9,000 holding on
 * the same scale, and the share price would be a flat line along the bottom.
 *
 * ==========================================================================================
 * IT PLOTS EVERY POINT IT HAS, INCLUDING BACKFILLED ONES
 * ==========================================================================================
 *
 * The prices arrive newest-first and are reversed here. Gaps are left as gaps rather than
 * interpolated — a household that recorded a price in January and another in August has two facts,
 * and a straight line between them would be a third thing nobody observed.
 */

export type PriceChartMode = "price" | "holding";

export interface PriceChartProps {
  prices: readonly PriceDto[];
  transactions: readonly TransactionDto[];
  mode: PriceChartMode;
}

/** Shares owned on a date, from the transactions this view already has. */
function sharesOn(transactions: readonly TransactionDto[], date: string): number {
  return transactions
    .filter((txn) => txn.date <= date)
    .reduce((held, txn) => held + Number(txn.shares) * (txn.kind === "buy" ? 1 : -1), 0);
}

export function PriceChart({ prices, transactions, mode }: PriceChartProps): ReactNode {
  const t = useT();
  const oldestFirst = [...prices].reverse();

  const series: ChartSeries[] =
    mode === "price"
      ? [
          {
            id: "price",
            label: t("inv.price.heading"),
            points: oldestFirst.map((price) => ({
              at: price.asOfDate,
              value: Number(price.price),
            })),
          },
        ]
      : [
          {
            id: "holding",
            label: t("inv.price.holdingWorth"),
            points: oldestFirst.map((price) => ({
              at: price.asOfDate,
              /*
               * Rounded to cents HERE, for display only. The exact arithmetic is the server's — this
               * is a chart, and a chart is drawn from doubles whatever it is given. Nothing computed
               * in this file ever reaches a figure the household is asked to reconcile.
               */
              value:
                Math.round(sharesOn(transactions, price.asOfDate) * Number(price.price) * 100) /
                100,
            })),
          },
        ];

  return (
    <StackedAreaChart
      series={series}
      markers={transactionMarkers(transactions, oldestFirst)}
      variant="lines"
      emptyMessage={t("inv.price.chartEmpty")}
    />
  );
}

/**
 * A mark on the nearest plotted date at or after each trade.
 *
 * SNAPPED TO A PLOTTED POINT, because a reference line at an x-value the axis does not contain is
 * silently dropped by the chart — the trade would simply not appear, with nothing to say it had been
 * left out. A trade after the last price has nowhere to go and is omitted deliberately.
 */
function transactionMarkers(
  transactions: readonly TransactionDto[],
  oldestFirst: readonly PriceDto[],
): ChartMarker[] {
  return transactions
    .map((txn) => {
      const point = oldestFirst.find((price) => price.asOfDate >= txn.date);
      if (!point) return null;
      return {
        at: point.asOfDate,
        label: txn.kind === "buy" ? t("inv.price.bought") : t("inv.price.sold"),
        direction: txn.kind === "buy" ? ("in" as const) : ("out" as const),
      };
    })
    .filter((marker): marker is ChartMarker => marker !== null);
}
